import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { LoadingSpaceType } from 'src/app/core/types';
import { validateZero } from 'src/app/core/validators/validators';
import { Project } from '../../../../core/objects/project';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gl-loading-space-set',
    templateUrl: './loading-space-set.component.html',
    styleUrls: ['./loading-space-set.component.scss'],
    standalone: false
})
export class LoadingSpaceSetComponent implements OnInit, OnChanges, OnDestroy {
  @Output() public submitEvent = new EventEmitter();
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  @Input() public loadingSpace: LoadingSpaceType;
  @Input() public project: Project;
  @Input() public typeLoadingSpace: number;
  @Input() public isEdit = false;
  public form: SetForm;
  public loadingSpaces: LoadingSpaceType[];
  public loadingSpaceMod: LoadingSpaceType;
  private subscription: Subscription;

  constructor() {
    this.form = this.generateLoadingSpaceForm();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public ngOnInit() {
    this.updateSpaceMod();
    this.onChosen(this.loadingSpace);
    if (this.isEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }

    this.subscription = this.form.valueChanges.subscribe((changes) => {
      if (changes.height1 && changes.length1 && changes.width1) {
        this.updateSpaceMod();
      }

      this.onSubmit();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.loadingSpace &&
      changes.loadingSpace.currentValue &&
      changes.loadingSpace.currentValue.height
    ) {
      this.updateSpaceMod();
    }
    if (changes.chosenLoadingSpace) {
      this.onChosen(changes.chosenLoadingSpace.currentValue as LoadingSpaceType);
    }
    if (changes.typeLoadingSpace && !changes.typeLoadingSpace.firstChange) {
      this.form.reset(undefined, { onlySelf: true, emitEvent: false });
    }
    if (changes.isEdit && changes.isEdit.currentValue) {
      this.isEdit = true;
      this.form.enable();
    } else if (changes.isEdit && !changes.isEdit.currentValue) {
      this.isEdit = false;
      this.form.disable();
    }
  }

  public reload(): void {
    this.onChosen(this.chosenLoadingSpace);
    this.onSubmit();
  }

  public onSubmit(): void {
    if (this.form.valid) {
      const setValue = {
        ...this.form.value,
        set: true,
      };
      if (this.isTrailer()) {
        delete setValue.semiTrailerLoadingSide;
      }

      this.submitEvent.emit(setValue);
    }
  }

  private updateSpaceMod(): void {
    if (!this.loadingSpace) {
      return;
    }
    const width1 =
      this.form.get('width1').value !== null ? parseFloat(this.form.get('width1').value) : null;
    const height1 =
      this.form.get('height1').value !== null ? parseFloat(this.form.get('height1').value) : null;
    const length1 =
      this.form.get('length1').value !== null ? parseFloat(this.form.get('length1').value) : null;
    const width = this.loadingSpace.width;
    const height = this.loadingSpace.height;
    const length = this.loadingSpace.length;
    this.loadingSpaceMod = {
      name: this.loadingSpace.name,
      length: length,
      width: width,
      height: height,
      limit: this.loadingSpace.limit,
      length1: length1 !== null ? length1 : null,
      width1: width1 !== null ? width1 : null,
      height1: height1 !== null ? height1 : null,
      limit1: parseFloat(this.form.get('limit1').value),
      set: true,
      type: this.loadingSpace.type,
      id: null,
      unit: this.loadingSpace.unit,
      axis: [],
      useInRecommendation: null,
      wheelTrailerWidth: null,
      wheelTrailerHeight: null,
      wheelTrailerLength: null,
      wheelTrailerDistance: null,
      wheelSemiTrailerWidth: null,
      wheelSemiTrailerHeight: null,
      wheelSemiTrailerLength: null,
      wheelSemiTrailerDistance: null,
      coolerTrailerWidth: null,
      coolerTrailerHeight: null,
      coolerTrailerLength: null,
      coolerTrailerFloorHeight: null,
      coolerSemiTrailerWidth: null,
      coolerSemiTrailerHeight: null,
      coolerSemiTrailerLength: null,
      coolerSemiTrailerFloorHeight: null,
      varioTrailerFloorHeight: null,
      varioSemiTrailerFloorHeight: null,
      trailerLoadingSide: null,
      semiTrailerLoadingSide: null,
    };
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    if (!loadingSpace) {
      return;
    }
    this.form.patchValue({
      length1: loadingSpace.set ? loadingSpace.length1 : null,
      height1: loadingSpace.set ? loadingSpace.height1 : null,
      width1: loadingSpace.set ? loadingSpace.width1 : null,
      limit1: loadingSpace.set ? loadingSpace.limit1 : null,
      semiTrailerLoadingSide: loadingSpace.semiTrailerLoadingSide
        ? loadingSpace.semiTrailerLoadingSide
        : null,
    });
    this.updateSpaceMod();
  }

  public formReset(): void {
    this.form.patchValue({
      length1: null,
      height1: null,
      width1: null,
      limit1: null,
      semiTrailerLoadingSide: null,
    });
    const setValue = {
      ...this.form.value,
      set: false,
    };
    if (this.isTrailer()) {
      delete setValue.semiTrailerLoadingSide;
    }
    this.submitEvent.emit(setValue);
  }

  private isTrailer(): boolean {
    return this.typeLoadingSpace === 1 || this.loadingSpace?.type === 1;
  }

  private generateLoadingSpaceForm(): SetForm {
    return new UntypedFormGroup({
      length1: new UntypedFormControl(null, [Validators.required, validateZero]),
      width1: new UntypedFormControl(null, [Validators.required, validateZero]),
      height1: new UntypedFormControl(null, [Validators.required, validateZero]),
      limit1: new UntypedFormControl(null, []),
      semiTrailerLoadingSide: new UntypedFormControl(null, []),
    });
  }
}

interface SetForm extends UntypedFormGroup {
  value: {
    length1: number;
    height1: number;
    width1: number;
    limit1: number;
    semiTrailerLoadingSide: string;
  };
}
